import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

interface OptionItem {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  bgColor: string;
  iconColor: string;
  path: string;
}

const DISCOVERY_OPTIONS: OptionItem[] = [
  {
    id: 'event',
    title: 'Cari event',
    subtitle: 'Mabar, sparing & turnamen di sekitarmu',
    icon: 'ph:calendar-blank-bold',
    bgColor: 'bg-orange-50 border-orange-100',
    iconColor: 'text-[#FC480D]',
    path: '/discovery/event',
  },
  {
    id: 'community',
    title: 'Cari komunitas',
    subtitle: 'Gabung grup yang rutin main tiap pekan',
    icon: 'ph:users-three-bold',
    bgColor: 'bg-blue-50 border-blue-100',
    iconColor: 'text-blue-600',
    path: '/discovery/community',
  },
  {
    id: 'booking',
    title: 'Booking lapangan',
    subtitle: 'Sewa lapangan sesuai jam & lokasi',
    icon: 'ph:buildings-bold',
    bgColor: 'bg-emerald-50 border-emerald-100',
    iconColor: 'text-emerald-600',
    path: '/discovery/arena',
  },
];

interface DesktopCardItem {
  id: string;
  title: string;
  subtitle: string;
  tag: string;
  icon: string;
  bgColor: string;
  iconColor: string;
  accentGradient: string;
  hoverBorder: string;
  btnHoverBg: string;
  path: string;
  actionText: string;
  highlights: {
    icon: string;
    text: string;
  }[];
}

const DESKTOP_DISCOVERY_CARDS: DesktopCardItem[] = [
  {
    id: 'event',
    title: 'Cari event',
    subtitle: 'Mabar, sparing & turnamen di sekitarmu yang siap diikuti.',
    tag: 'Mabar & Sparing',
    icon: 'ph:calendar-blank-bold',
    bgColor: 'bg-orange-50 border-orange-100',
    iconColor: 'text-[#FC480D]',
    accentGradient: 'from-[#FC480D] via-amber-500 to-orange-400',
    hoverBorder: 'hover:border-[#FC480D]/40',
    btnHoverBg: 'group-hover:bg-[#FC480D]',
    path: '/discovery/event',
    actionText: 'Jelajahi Event Mabar',
    highlights: [
      { icon: 'ph:tennis-ball-bold', text: 'Badminton, Padel, Mini Soccer, Tennis & Pickleball' },
      { icon: 'ph:chart-line-up-bold', text: 'Matchmaking seimbang berdasarkan level / MMR' },
      { icon: 'ph:users-bold', text: 'Cek sisa kuota pemain & join instan tanpa repot' },
    ],
  },
  {
    id: 'community',
    title: 'Cari komunitas',
    subtitle: 'Gabung grup yang rutin main tiap pekan dan bangun koneksi baru.',
    tag: 'Komunitas & Klub',
    icon: 'ph:users-three-bold',
    bgColor: 'bg-blue-50 border-blue-100',
    iconColor: 'text-blue-600',
    accentGradient: 'from-blue-600 via-indigo-500 to-sky-400',
    hoverBorder: 'hover:border-blue-400/40',
    btnHoverBg: 'group-hover:bg-blue-600',
    path: '/discovery/community',
    actionText: 'Temukan Komunitas',
    highlights: [
      { icon: 'ph:seal-check-bold', text: 'Ratusan komunitas olahraga verified di berbagai kota' },
      { icon: 'ph:calendar-check-bold', text: 'Jadwal main rutin mingguan terorganisir rapi' },
      { icon: 'ph:trophy-bold', text: 'Statistik pertandingan & leaderboard internal grup' },
    ],
  },
  {
    id: 'booking',
    title: 'Booking lapangan',
    subtitle: 'Sewa lapangan sesuai jam, jenis olahraga & lokasi pilihanmu.',
    tag: 'Sewa Venue & Lapangan',
    icon: 'ph:buildings-bold',
    bgColor: 'bg-emerald-50 border-emerald-100',
    iconColor: 'text-emerald-600',
    accentGradient: 'from-emerald-600 via-teal-500 to-emerald-400',
    hoverBorder: 'hover:border-emerald-400/40',
    btnHoverBg: 'group-hover:bg-emerald-600',
    path: '/discovery/arena',
    actionText: 'Cek Jadwal Lapangan',
    highlights: [
      { icon: 'ph:map-pin-bold', text: 'Pilihan arena lengkap dengan foto & info fasilitas' },
      { icon: 'ph:clock-bold', text: 'Ketersediaan slot jam kosong secara real-time' },
      { icon: 'ph:credit-card-bold', text: 'Konfirmasi instan dengan opsi pembayaran digital' },
    ],
  },
];

const ClientDiscoveryPage: React.FC = () => {
  const navigate = useNavigate();

  // Bottom Sheet Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);

  // Show modal automatically on page load (mobile view only)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isDesktop =
      window.innerWidth >= 768 ||
      (window.matchMedia ? window.matchMedia('(min-width: 768px)').matches : false);

    if (isDesktop) return;

    const timer = setTimeout(() => {
      setIsModalOpen(true);
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const handleCloseModal = () => {
    if (isModalClosing) return;
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
    }, 280);
  };

  const handleOptionClick = (path: string) => {
    if (isModalOpen) {
      handleCloseModal();
      setTimeout(() => {
        navigate(path);
      }, 200);
    } else {
      navigate(path);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-24 md:pb-12">
      {/* Desktop View */}
      <div className="hidden md:block max-w-6xl mx-auto px-6 pt-6 pb-8 space-y-8 animate-fade-in">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-white to-orange-50/50 border border-[#E0E6ED] p-8 lg:p-9 shadow-xs">
          {/* Subtle background ambient light */}
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-gradient-to-br from-orange-200/30 to-amber-200/10 blur-3xl pointer-events-none" />
          <div className="absolute right-8 bottom-0 opacity-10 pointer-events-none hidden lg:block">
            <Icon icon="ph:compass" className="w-56 h-56 text-[#FC480D]" />
          </div>

          <div className="relative z-10 max-w-2xl space-y-3.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200/80 text-[#FC480D] text-xs font-bold tracking-wide">
              <Icon icon="ph:compass-fill" className="w-4 h-4" />
              <span>PUSAT EKSPLORASI SPORTO</span>
            </div>

            <h1 className="text-2xl lg:text-3xl font-extrabold text-[#0E1726] tracking-tight">
              Mau cari apa hari ini?
            </h1>

            <p className="text-xs md:text-sm text-gray-500 font-medium leading-relaxed">
              Jelajahi berbagai pilihan aktivitas olahraga: temukan sesi mabar terdekat, gabung komunitas aktif, atau booking lapangan favoritmu secara instan.
            </p>

            <div className="flex flex-wrap items-center gap-2.5 pt-2 text-xs text-gray-600 font-medium">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gray-50 border border-gray-200/60">
                <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5 text-[#FC480D]" />
                Slot Terverifikasi & Live
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gray-50 border border-gray-200/60">
                <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5 text-blue-600" />
                Komunitas Olahraga Aktif
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gray-50 border border-gray-200/60">
                <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5 text-emerald-600" />
                Konfirmasi Booking Instan
              </span>
            </div>
          </div>
        </div>

        {/* 3 Main Discovery Cards */}
        <div className="grid grid-cols-3 gap-6">
          {DESKTOP_DISCOVERY_CARDS.map((card) => (
            <div
              key={card.id}
              onClick={() => handleOptionClick(card.path)}
              className={`group bg-white rounded-3xl border border-[#E0E6ED] ${card.hoverBorder} p-7 shadow-xs hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 cursor-pointer flex flex-col justify-between relative overflow-hidden`}
            >
              <div className="space-y-5">
                {/* Icon & Tag */}
                <div className="flex items-center justify-between">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${card.bgColor} ${card.iconColor} group-hover:scale-105 transition-transform duration-200 shadow-2xs`}
                  >
                    <Icon icon={card.icon} className="w-7 h-7" />
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 group-hover:bg-gray-50 transition-colors">
                    {card.tag}
                  </span>
                </div>

                {/* Title & Description */}
                <div className="space-y-1.5">
                  <h2 className="text-lg font-bold text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                    {card.title}
                  </h2>
                  <p className="text-xs text-gray-500 font-medium leading-relaxed">
                    {card.subtitle}
                  </p>
                </div>

                {/* Highlights List */}
                <div className="space-y-2.5 pt-3 border-t border-gray-100">
                  {card.highlights.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 text-xs text-gray-600 font-medium">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${card.iconColor}`}>
                        <Icon icon={item.icon} className="w-3.5 h-3.5" />
                      </div>
                      <span className="leading-snug">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-6">
                <div
                  className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold text-[#0E1726] bg-gray-100/90 group-hover:text-white ${card.btnHoverBg} transition-all duration-200 flex items-center justify-center gap-2 shadow-2xs`}
                >
                  <span>{card.actionText}</span>
                  <Icon
                    icon="ph:arrow-right-bold"
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        {/* Main Page Body */}
        <div className="max-w-2xl mx-auto px-4 pt-5 md:pt-8 space-y-4 animate-fade-in">
          {/* Title */}
          <h1 className="text-lg md:text-xl font-bold text-[#0E1726]">
            Mau cari apa hari ini?
          </h1>

          {/* Options List */}
          <div className="space-y-3">
            {DISCOVERY_OPTIONS.map((opt) => (
              <div
                key={opt.id}
                onClick={() => handleOptionClick(opt.path)}
                className="w-full p-4 rounded-2xl bg-white border border-[#E0E6ED] shadow-2xs hover:shadow-xs flex items-center justify-between transition-all cursor-pointer group active:scale-[0.99]"
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${opt.bgColor} ${opt.iconColor}`}
                  >
                    <Icon icon={opt.icon} className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-[#0E1726] group-hover:text-[#FC480D] transition-colors truncate">
                      {opt.title}
                    </h3>
                    <p className="text-xs text-gray-400 font-medium truncate mt-0.5">
                      {opt.subtitle}
                    </p>
                  </div>
                </div>
                <Icon
                  icon="ph:caret-right-bold"
                  className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Initial Bottom Sheet Popup Modal */}
        {isModalOpen && (
          <div
            className={`fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center transition-opacity duration-300 ease-in-out ${
              isModalClosing ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-in fade-in duration-200'
            }`}
            onClick={handleCloseModal}
          >
            <div
              className={`w-full max-w-md bg-white rounded-t-3xl p-5 sm:p-6 flex flex-col gap-4 shadow-2xl transition-transform duration-300 ease-in-out ${
                isModalClosing
                  ? 'translate-y-full'
                  : 'translate-y-0 animate-in slide-in-from-bottom duration-300'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle bar */}
              <div className="w-12 h-1 rounded-full bg-gray-300 self-center -mt-1 mb-1" />

              {/* Header Text */}
              <div className="space-y-1">
                <h2 className="text-lg sm:text-xl font-bold text-[#0E1726]">
                  Mau cari apa?
                </h2>
                <p className="text-xs text-gray-400 font-medium">
                  Pilih satu untuk mulai menjelajah
                </p>
              </div>

              {/* Options in Modal */}
              <div className="space-y-3 pt-1">
                {DISCOVERY_OPTIONS.map((opt) => (
                  <div
                    key={opt.id}
                    onClick={() => handleOptionClick(opt.path)}
                    className="w-full p-4 rounded-2xl bg-white border border-[#E0E6ED] shadow-2xs flex items-center justify-between transition-all cursor-pointer group active:scale-[0.99]"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${opt.bgColor} ${opt.iconColor}`}
                      >
                        <Icon icon={opt.icon} className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-[#0E1726] group-hover:text-[#FC480D] transition-colors truncate">
                          {opt.title}
                        </h3>
                        <p className="text-xs text-gray-400 font-medium truncate mt-0.5">
                          {opt.subtitle}
                        </p>
                      </div>
                    </div>
                    <Icon
                      icon="ph:caret-right-bold"
                      className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0"
                    />
                  </div>
                ))}
              </div>

              {/* Bottom Dismiss Action */}
              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="text-xs font-semibold text-gray-500 hover:text-[#0E1726] transition-colors py-2 px-4 rounded-full cursor-pointer"
                >
                  Nanti saja
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDiscoveryPage;
